import type {
  GroomingMetrics,
  GroomingPrepCoverageBatch,
  GroomingSizedItem,
  GroomingSprintUnavailableReason,
} from "@/lib/api/grooming";

/**
 * "How grooming is going" (epic PI-SPEC-SW-E293, feature PI-SPEC-SW-1504,
 * UX-PM-01, frames E1 and E4): the words, dates and remembered open state of
 * the Backlog's progress panel. React-free.
 *
 * Every line is a count in words ("7 of 9"), never a percentage: the API's
 * ratios (`share`, `coverage`) are never shown. Claude's sizes are advisory
 * and appear only for items the team has already sized.
 */

/** The look-back windows the switch offers, in days (01-roles.md section 9.6). */
export const PROGRESS_WINDOWS = [30, 60, 90] as const;
export type ProgressWindow = (typeof PROGRESS_WINDOWS)[number];

/** The window shown first: the last 60 days. */
export const PROGRESS_WINDOW_DEFAULT: ProgressWindow = 60;

/** Below this many sized items the sizing line says it is too early to judge (frame E4, 01-roles.md section 9.6). */
export const SIZING_JUDGE_FROM = 5;

/** The sentence shown instead of the legend below {@link SIZING_JUDGE_FROM} sized items. */
export const SIZING_TOO_EARLY =
  "Too few sized items to judge yet. This becomes meaningful from about 5; it fills in as the team sizes items in meetings.";

/** The footnote under the three lines. */
export const PROGRESS_FOOTNOTE =
  "Claude's sizes are suggestions; the team decides and types the points in ADO. The Toolbox never writes Story Points.";

/** Who sees the panel (the `read-grooming-progress` roles). */
export const PROGRESS_AUDIENCE = "Seen by PMs, Admins, POs and Viewers";

/** localStorage key of the panel's remembered open state ('1' open, '0' closed). */
export const PROGRESS_PANEL_STORAGE_KEY = "spectree.grooming.progressPanelOpen";

/**
 * Whether the panel is open: the person's last choice in this browser or app,
 * open when nothing is stored (the Backlog's right-hand space is otherwise
 * empty until an item is picked), and open when storage is unavailable.
 */
export function readProgressPanelOpen(): boolean {
  try {
    return window.localStorage.getItem(PROGRESS_PANEL_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

/** Remember open or closed; a storage failure only means it is not remembered. */
export function writeProgressPanelOpen(open: boolean): void {
  try {
    window.localStorage.setItem(PROGRESS_PANEL_STORAGE_KEY, open ? "1" : "0");
  } catch {
    // Not remembered; the page still uses the choice for this visit.
  }
}

export function isProgressWindow(value: unknown): value is ProgressWindow {
  return typeof value === "number" && (PROGRESS_WINDOWS as readonly number[]).includes(value);
}

const MONTH_DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

/** "Oct 20" for a local date. */
export function monthDay(date: Date): string {
  return MONTH_DAY.format(date);
}

/**
 * A calendar day as a local date. A `YYYY-MM-DD` prefix (a session date, or a
 * sprint's start or finish, which ADO sends at midnight UTC) is read as that
 * day wherever the reader is; anything else is an instant in local time.
 * Null for a missing or unreadable value.
 */
export function calendarDay(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T00:00:00(?:\.0+)?Z?$)/.exec(value);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Oct 20" for a calendar day, or "" when it is missing. */
export function dayWords(value: string | null | undefined): string {
  const day = calendarDay(value);
  return day ? monthDay(day) : "";
}

/** The window's dates under the switch: "Aug 21 – Oct 20" (en dash), since = today − sinceDays, local time. */
export function windowDates(sinceDays: number, now: Date = new Date()): string {
  const since = new Date(now.getFullYear(), now.getMonth(), now.getDate() - sinceDays);
  return `${monthDay(since)} – ${monthDay(now)}`;
}

/** "7 of 9". */
export function countOf(part: number, whole: number): string {
  return `${String(part)} of ${String(whole)}`;
}

/** "1 item" / "2 items". */
export function itemsWord(count: number): string {
  return `${String(count)} ${count === 1 ? "item" : "items"}`;
}

/** "1 session" / "3 sessions". */
export function sessionsWord(count: number): string {
  return `${String(count)} ${count === 1 ? "session" : "sessions"}`;
}

// ---------------------------------------------------------------------------
// Sizing
// ---------------------------------------------------------------------------

export type SizingLine =
  /** No sized items: "No sized items yet", 5 empty slots, the too-early sentence, no link. */
  | { kind: "none"; slots: number }
  /** 1-4 sized items: the count, the dots, empty slots up to 5, the too-early sentence and the link. */
  | { kind: "early"; inside: number; count: number; slots: number }
  /** 5 or more: the count, the dots, the legend and the link. */
  | { kind: "judged"; inside: number; count: number; slots: 0 };

/** Which sizing line to show for the counted items. */
export function sizingLine(agreement: GroomingMetrics["estimateAgreement"]): SizingLine {
  const count = agreement.items.length;
  const inside = agreement.items.filter((item) => item.landed === "inside").length;
  if (count === 0) return { kind: "none", slots: SIZING_JUDGE_FROM };
  if (count < SIZING_JUDGE_FROM) return { kind: "early", inside, count, slots: SIZING_JUDGE_FROM - count };
  return { kind: "judged", inside, count, slots: 0 };
}

/** A dot's tooltip: "AB#24755 Login fails on Safari: team 3, Claude 2–5" ("AB#24755: …" without a title). */
export function sizedDotTitle(item: GroomingSizedItem): string {
  const name = item.title ? `AB#${item.itemId} ${item.title}` : `AB#${item.itemId}`;
  return `${name}: team ${String(item.teamPoints)}, Claude ${String(item.pointsLow)}–${String(item.pointsHigh)}`;
}

// ---------------------------------------------------------------------------
// Preparation
// ---------------------------------------------------------------------------

/** The line under the preparation count: "agenda items had a brief before the meeting, over 3 sessions". */
export function preparationLine(sessions: number): string {
  return `agenda items had a brief before the meeting, over ${sessionsWord(sessions)}`;
}

/** The date of a finished session's row: its meeting date, else when it finished ("Oct 15"). */
export function sessionDay(batch: Pick<GroomingPrepCoverageBatch, "sessionDate" | "finishedAt">): string {
  return dayWords(batch.sessionDate ?? batch.finishedAt);
}

/**
 * The sessions newest first by the day the reader sees (`sessionDay`: the
 * meeting date, else the run's finish in local time), then the later-finished
 * run. The API sorts an undated run by its UTC finish day, which near
 * midnight is not the day shown here (Copilot review of #545). A copy.
 */
export function sessionsNewestFirst<T extends Pick<GroomingPrepCoverageBatch, "sessionDate" | "finishedAt">>(batches: readonly T[]): T[] {
  const dayKey = (batch: T): number => {
    const day = calendarDay(batch.sessionDate ?? batch.finishedAt);
    return day ? day.getFullYear() * 10_000 + (day.getMonth() + 1) * 100 + day.getDate() : 0;
  };
  const finished = (batch: T): number => (batch.finishedAt ? Date.parse(batch.finishedAt) || 0 : 0);
  return [...batches].sort((a, b) => dayKey(b) - dayKey(a) || finished(b) - finished(a));
}

/** How much of a session's bar is filled, 0 to 1 (a width, never shown as text). */
export function sessionFill(batch: Pick<GroomingPrepCoverageBatch, "items" | "briefedOrCurrent">): number {
  if (batch.items <= 0) return 0;
  return Math.min(1, Math.max(0, batch.briefedOrCurrent / batch.items));
}

/** "No finished sessions in the last 60 days." */
export function noSessionsLine(sinceDays: number): string {
  return `No finished sessions in the last ${String(sinceDays)} days.`;
}

// ---------------------------------------------------------------------------
// This sprint
// ---------------------------------------------------------------------------

export type SprintLine =
  | { kind: "entered"; count: number; sprint: string; dates: string }
  | { kind: "none-entered"; sprint: string; dates: string }
  | { kind: "no-sprint" }
  | { kind: "unavailable"; reason: GroomingSprintUnavailableReason | null };

/** The sentence for a sprint that cannot be read. */
export const SPRINT_UNAVAILABLE_WORDS: Readonly<Record<GroomingSprintUnavailableReason, string>> = {
  ADO_PAT_INVALID: "This sprint can't be read: Azure DevOps refused your token. The other two lines still count.",
  ADO_PAT_REQUIRED: "This sprint can't be read without your Azure DevOps token. The other two lines still count.",
};

/** When the count is missing for no stated reason. */
export const SPRINT_UNREADABLE = "This sprint can't be read right now. The other two lines still count.";

/** The sentence when the team has no current sprint. */
export const NO_CURRENT_SPRINT = "This team has no current sprint in Azure DevOps.";

/** A sprint's dates in words: "Oct 13 – Oct 24", one date when only one is known, "" when none. */
export function sprintDates(iteration: { startDate: string; finishDate: string }): string {
  const start = dayWords(iteration.startDate);
  const finish = dayWords(iteration.finishDate);
  return start && finish ? `${start} – ${finish}` : start || finish;
}

/**
 * The metrics with This sprint counted from the Backlog read's exceptions, so the panel's line, the
 * sprint items view and the copied summary always agree with the rows the view lists. The metrics
 * read's own count comes from a separate live ADO read made at another moment; its states (no
 * current sprint, the token refused) are kept as they are.
 */
export function withBacklogSprint(metrics: GroomingMetrics, exceptions: number): GroomingMetrics {
  if (metrics.sprint.unavailableReason || !metrics.sprint.currentIteration) return metrics;
  if (metrics.ungroomedSprintEntries === exceptions) return metrics;
  return { ...metrics, ungroomedSprintEntries: exceptions };
}

/** Which sprint line to show. */
export function sprintLine(metrics: Pick<GroomingMetrics, "ungroomedSprintEntries" | "sprint">): SprintLine {
  const { unavailableReason, currentIteration } = metrics.sprint;
  if (unavailableReason) return { kind: "unavailable", reason: unavailableReason };
  if (!currentIteration) return { kind: "no-sprint" };
  const count = metrics.ungroomedSprintEntries;
  if (count === null) return { kind: "unavailable", reason: null };
  const sprint = currentIteration.name;
  const dates = sprintDates(currentIteration);
  return count > 0 ? { kind: "entered", count, sprint, dates } : { kind: "none-entered", sprint, dates };
}

/** "Sprint 14 · Oct 13 – Oct 24" (just the name when the sprint has no dates). */
export function sprintCaption(line: { sprint: string; dates: string }): string {
  return line.dates ? `${line.sprint} · ${line.dates}` : line.sprint;
}

/** Matches a percentage; nothing the panel renders may match it. */
export const PERCENT_PATTERN = /\d+\s*%/;

// ---------------------------------------------------------------------------
// The items behind each line (feature PI-SPEC-SW-1505, frame E2)
// ---------------------------------------------------------------------------

/** The lines whose items open as a Backlog content view (`?progress=`). */
export const PROGRESS_ITEMS_KINDS = ["sizing", "preparation", "sprint"] as const;
export type ProgressItemsKind = (typeof PROGRESS_ITEMS_KINDS)[number];

/** The Backlog's search param that opens the items behind a line; independent of `?stage=`. */
export const PROGRESS_ITEMS_PARAM = "progress";

/** The items view named by `?progress=`, or null when it is missing or not a line. */
export function progressItemsKindOf(value: string | null): ProgressItemsKind | null {
  return (PROGRESS_ITEMS_KINDS as readonly string[]).includes(value ?? "") ? (value as ProgressItemsKind) : null;
}

/** "1 point" / "3 points". */
export function pointsWord(points: number): string {
  return `${String(points)} ${points === 1 ? "point" : "points"}`;
}

/** Claude's range in words: "2 – 5" (en dash), or "3" when low and high are the same. */
export function rangeWords(item: Pick<GroomingSizedItem, "pointsLow" | "pointsHigh">): string {
  return item.pointsLow === item.pointsHigh
    ? String(item.pointsLow)
    : `${String(item.pointsLow)} – ${String(item.pointsHigh)}`;
}

/** Where the team's size landed, in words (always beside an icon, never colour alone). */
export const LANDED_WORDS: Readonly<Record<GroomingSizedItem["landed"], string>> = {
  inside: "Inside",
  above: "Above the range",
  below: "Below the range",
};

/** An item's name in the progress views: its title, else the loaded Backlog's title for it, else `AB#<id>`. */
export function sizedItemName(item: Pick<GroomingSizedItem, "itemId" | "title">, backlogTitle?: string): string {
  return item.title ?? backlogTitle ?? `AB#${item.itemId}`;
}

/** The sizing view's heading: "Sizing: 7 of 9 inside Claude's range". */
export function sizingItemsHeading(agreement: GroomingMetrics["estimateAgreement"]): string {
  const line = sizingLine(agreement);
  if (line.kind === "none") return "Sizing: no items sized yet";
  return `Sizing: ${countOf(line.inside, line.count)} inside Claude's range`;
}

/** The sizing view's subtitle, with the window's dates. */
export function sizingItemsSubtitle(sinceDays: number, now: Date = new Date()): string {
  return `Items whose brief was written ${windowDates(sinceDays, now)} and that the team has since sized in ADO · newest brief per item`;
}

/** The preparation view's heading: "Preparation: 8 of 8 agenda items had a brief". */
export function preparationItemsHeading(coverage: GroomingMetrics["prepCoverage"]): string {
  if (coverage.batches.length === 0) return "Preparation: no finished sessions";
  return `Preparation: ${countOf(coverage.briefedOrCurrent, coverage.items)} agenda items had a brief`;
}

/** The preparation view's subtitle, with the window's dates. */
export function preparationItemsSubtitle(sinceDays: number, now: Date = new Date()): string {
  return `Sessions that finished ${windowDates(sinceDays, now)} · newest first · agenda items in meeting order`;
}

/** A session group's line: "3 of 3 had a brief before the meeting". */
export function sessionGroupLine(batch: Pick<GroomingPrepCoverageBatch, "items" | "briefedOrCurrent">): string {
  return `${countOf(batch.briefedOrCurrent, batch.items)} had a brief before the meeting`;
}

/** The sprint view's heading and subtitle, from the Backlog read's current sprint and exceptions. */
export function sprintItemsHeading(
  currentIteration: { name: string; startDate: string; finishDate: string } | null,
  count: number
): { heading: string; subtitle: string } {
  if (!currentIteration) return { heading: "This sprint: the team has no current sprint", subtitle: "" };
  const subtitle = sprintCaption({ sprint: currentIteration.name, dates: sprintDates(currentIteration) });
  const heading =
    count === 0
      ? `This sprint: nothing entered ${currentIteration.name} ungroomed`
      : `This sprint: ${itemsWord(count)} entered ${currentIteration.name} without being groomed`;
  return { heading, subtitle };
}

/** The note under the sizing table (frame E2). */
export const SIZING_ITEMS_NOTE =
  "\"Outside\" is not a mistake by anyone: it shows where Claude's reading of the code and the team's knowledge differed. Open an item to read its brief and the team's discussion in ADO. Sizes stay advisory; the Toolbox never writes Story Points.";
